/**
 * usePortalUnseen — R2: the header "your submissions" unseen-update badge.
 * SPEC-stakeholder-portal-presentation-redesign-2026-07-20, R2.
 *
 * Mirrors useGlobalMessages' polling shape (useGlobalUnreadMessages): a
 * react-query hook that polls a small unread-style summary. The write side
 * (mark-seen) is deliberately NOT optimistic — the badge only clears once the
 * server has CONFIRMED the write (AC4: on a write failure the badge stays, no
 * silent desync).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export const PORTAL_UNSEEN_QUERY_KEY = ['stakeholder-portal', 'unseen-count'] as const;

/**
 * Polls the caller's unseen-submission-update count. `enabled` gates the
 * query so an ineligible user (no stakeholder_portal capability, not a
 * super-admin) never fires a request against the backend-gated endpoint.
 */
export function usePortalUnseenCount(enabled: boolean) {
  return useQuery({
    queryKey: PORTAL_UNSEEN_QUERY_KEY,
    queryFn: async () => {
      const response = await api.stakeholderPortal.unseenCount();
      return response.unseen_count;
    },
    enabled,
    // Refresh every 60 seconds — mirrors useGlobalUnreadMessages; a badge
    // count is non-critical, near-real-time is enough.
    staleTime: 60_000,
    refetchInterval: enabled ? 60_000 : false,
  });
}

/**
 * Advances the caller's last-seen marker (AC4). The badge is cleared by the
 * CALLER only after `onSuccess` fires — never optimistically before the write
 * is confirmed, and never at all on failure (the count query is left as-is,
 * so the next poll/refetch reflects reality rather than a guess).
 */
export function useMarkPortalSeen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.stakeholderPortal.markSeen(),
    onSuccess: () => {
      // v0.17.2: REFETCH, never assume zero. Marking seen clears the STATUS
      // axis, but an unanswered operator question deliberately survives the
      // seen-marker (models/stakeholder_portal.py::_has_unanswered_question),
      // so the true count after marking seen is whatever the server says.
      // Hard-zeroing here threw that away: verified in production on
      // 2026-09-19, the badge vanished on opening the panel and only came
      // back on a full reload, which reproduces the exact defect the
      // server-side fix exists to close.
      queryClient.invalidateQueries({ queryKey: PORTAL_UNSEEN_QUERY_KEY });
    },
  });
}
